import { supabase } from "@/integrations/supabase/client";

const BUCKET = "inquiry-photos";
const PUBLIC_PREFIX = `/storage/v1/object/public/${BUCKET}/`;

/**
 * Extract the storage path from a full public URL or return as-is if already a path.
 */
export function extractPhotoPath(urlOrPath: string): string {
  const idx = urlOrPath.indexOf(PUBLIC_PREFIX);
  if (idx !== -1) {
    return decodeURIComponent(urlOrPath.slice(idx + PUBLIC_PREFIX.length));
  }
  // Also handle signed URL format
  const signedPrefix = `/storage/v1/object/sign/${BUCKET}/`;
  const sIdx = urlOrPath.indexOf(signedPrefix);
  if (sIdx !== -1) {
    const pathPart = urlOrPath.slice(sIdx + signedPrefix.length);
    return decodeURIComponent(pathPart.split("?")[0]);
  }
  return urlOrPath;
}

/**
 * Get a signed URL for an inquiry photo. Falls back to the original URL on error.
 */
export async function getSignedPhotoUrl(urlOrPath: string, expiresIn = 3600): Promise<string> {
  const path = extractPhotoPath(urlOrPath);
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, expiresIn);
  if (error || !data?.signedUrl) {
    console.warn("Failed to get signed URL for", path, error);
    return urlOrPath; // fallback
  }
  return data.signedUrl;
}

/**
 * Get signed URLs for multiple photos in batch.
 */
export async function getSignedPhotoUrls(urlsOrPaths: string[], expiresIn = 3600): Promise<string[]> {
  if (urlsOrPaths.length === 0) return [];
  const paths = urlsOrPaths.map(extractPhotoPath);
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrls(paths, expiresIn);
  if (error || !data) {
    console.warn("Failed to get signed URLs", error);
    return urlsOrPaths;
  }
  return data.map((d, i) => d.signedUrl || urlsOrPaths[i]);
}

/**
 * Upload a photo and return the storage path (NOT full URL).
 */
export async function uploadInquiryPhoto(
  path: string,
  data: Blob | File,
  contentType = "image/jpeg"
): Promise<string> {
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, data, { contentType });
  if (error) throw error;
  return path;
}
